import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import FieldLabel from './FieldLabel'

const getTextAreaHeightForRows = (el: HTMLTextAreaElement, rows: number): number => {
  const styles = getComputedStyle(el)
  const lineHeight = parseFloat(styles.lineHeight)
  if (!Number.isFinite(lineHeight)) {
    return el.scrollHeight
  }
  const paddingTop = parseFloat(styles.paddingTop) || 0
  const paddingBottom = parseFloat(styles.paddingBottom) || 0
  const borderTop = parseFloat(styles.borderTopWidth) || 0
  const borderBottom = parseFloat(styles.borderBottomWidth) || 0
  return lineHeight * rows + paddingTop + paddingBottom + borderTop + borderBottom
}

const adjustTextAreaHeight = (el: HTMLTextAreaElement, maxRows: number, minRows = 1) => {
  if (el.offsetParent === null) return
  el.style.height = 'auto'
  const maxHeight = getTextAreaHeightForRows(el, maxRows)
  const minHeight = getTextAreaHeightForRows(el, minRows)
  const nextHeight = Math.max(minHeight, Math.min(el.scrollHeight, maxHeight))
  el.style.height = `${nextHeight}px`
  el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden'
}

type Props = {
  label?: string | React.ReactElement
  rows?: number
  tooltip?: string
  className?: string
  autoGrow?: boolean
  maxRows?: number
  minRows?: number
  value?: string
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement>
  optional?: boolean
  [x: string]: any
}

const TextArea: React.FC<Props> = ({
  label,
  tooltip,
  rows = 1,
  className,
  autoGrow = false,
  maxRows = 6,
  minRows = 1,
  value,
  onChange,
  required,
  optional,
  ...rest
}) => {
  const ref = useRef<HTMLTextAreaElement>(null)

  const syncHeight = useCallback(() => {
    if (ref.current && autoGrow) {
      adjustTextAreaHeight(ref.current, maxRows, minRows)
    }
  }, [autoGrow, maxRows, minRows])

  useLayoutEffect(() => {
    syncHeight()
  }, [value, syncHeight])

  useEffect(() => {
    if (!autoGrow || !ref.current) return
    const el = ref.current
    const observer = new ResizeObserver(() => adjustTextAreaHeight(el, maxRows, minRows))
    observer.observe(el)
    return () => observer.disconnect()
  }, [autoGrow, maxRows, minRows])

  useEffect(() => {
    if (!autoGrow) return
    window.addEventListener('resize', syncHeight)
    return () => window.removeEventListener('resize', syncHeight)
  }, [autoGrow, syncHeight])

  useEffect(() => {
    if (!autoGrow || !ref.current) return
    const el = ref.current
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        adjustTextAreaHeight(el, maxRows, minRows)
      }
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [autoGrow, maxRows, minRows])

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange?.(e)
    if (autoGrow) {
      adjustTextAreaHeight(e.target, maxRows, minRows)
    }
  }

  return (
    <>
      {label && <FieldLabel label={label} htmlFor={rest.id} tooltip={tooltip} required={required} optional={optional} />}
      <textarea
        ref={ref}
        className={`form-control ${className || ''}`.trim()}
        rows={autoGrow ? minRows : rows}
        onChange={handleChange}
        required={required}
        {...rest}
        {...(value !== undefined ? { value } : {})}
      />
    </>
  )
}

export default TextArea
